import { i18nService } from '../services/i18n';
import type { CoworkMessage } from '../types/cowork';

export type TodoStatus = 'completed' | 'in_progress' | 'pending' | 'unknown';

export type ParsedTodoItem = {
  primaryText: string;
  secondaryText: string | null;
  status: TodoStatus;
};

export const normalizeToolName = (value: string): string => value.toLowerCase().replace(/[\s_]+/g, '');

export const isTodoWriteToolName = (toolName: string | undefined): boolean => {
  if (!toolName) return false;
  return normalizeToolName(toolName) === 'todowrite';
};

export const toTrimmedString = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

export const normalizeTodoStatus = (value: unknown): TodoStatus => {
  const normalized = typeof value === 'string'
    ? value.trim().toLowerCase().replace(/-/g, '_')
    : '';

  if (normalized === 'completed') return 'completed';
  if (normalized === 'in_progress' || normalized === 'running') return 'in_progress';
  if (normalized === 'pending' || normalized === 'todo') return 'pending';
  return 'unknown';
};

export const parseTodoWriteItems = (input: unknown): ParsedTodoItem[] | null => {
  if (!input || typeof input !== 'object') return null;
  const record = input as Record<string, unknown>;
  // 兼容各种 OpenAI-compat 模型对 TodoWrite 入参的变体：
  // 1) 标准: { todos: [...] }
  // 2) MiniMax 变体 A: { todos: "[...]" }  ← 数组被 JSON.stringify 一层
  // 3) MiniMax 变体 B: { content: "[...]" } ← 直接把数组塞进 content 字段
  let todos: unknown = record.todos ?? record.content;
  if (typeof todos === 'string') {
    try {
      todos = JSON.parse(todos);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(todos)) return null;

  const parsedItems = todos
    .map((rawTodo) => {
      if (!rawTodo || typeof rawTodo !== 'object') {
        return null;
      }

      const todo = rawTodo as Record<string, unknown>;
      const activeForm = toTrimmedString(todo.activeForm) ?? toTrimmedString(todo.ActiveForm);
      const content = toTrimmedString(todo.content) ?? toTrimmedString(todo.Content);
      const primaryText = activeForm ?? content ?? i18nService.t('coworkTodoUntitled');
      const secondaryText = content && content !== primaryText ? content : null;

      return {
        primaryText,
        secondaryText,
        status: normalizeTodoStatus(todo.status ?? todo.Status),
      } satisfies ParsedTodoItem;
    })
    .filter((item): item is ParsedTodoItem => item !== null);

  return parsedItems.length > 0 ? parsedItems : null;
};

export const extractLatestTodoItems = (messages: CoworkMessage[]): ParsedTodoItem[] | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.type === 'tool_use' && isTodoWriteToolName(msg.metadata?.toolName as string | undefined)) {
      return parseTodoWriteItems(msg.metadata?.toolInput);
    }
  }
  return null;
};
